import type { FC, ReactElement } from "react"

import { AnimatePresence, m } from "framer-motion"
import eq from "lodash-es/eq"

import { ETypographyColor, Typography } from "@/3514/components"
import { THEME } from "@/3514/theme"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"
import { type TEmptyCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import { useCoachingChatConversationErrorSelector as useConversationErrorSelector } from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatMessageAuthor as EMessageAuthor,
    type TCoachingChatConversationError as TConversationError
} from "../types"

import { CoachingChatMessageRetrier as Retrier } from "./coaching-chat.message-retrier.component"
import { coachingChatOwlComponents as owls } from "./coaching-chat.owls-components"
import { CoachingChatTypingIndicator as TypingIndicator } from "./coaching-chat.typing-indicator.component"

const { isStringPlainText } = richTextRendererUtils

const {
    conversation: { message: classes }
} = chatStyles

const MessageStreamable: FC = (): ReactElement => {
    const {
        entityId,
        conversation: { feedRef, feedScrollerHook, streamerHook, streamingBorderHook, renderMessage }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    const { variant: errorVariant }: TConversationError = useConversationErrorSelector(entityId)

    const isStreamingFailedError: boolean = eq(errorVariant, EConversationErrorVariant.StreamingFailed)

    const {
        value: messageText,
        isProgressing,
        isDone,
        isDelayed,
        onStreamingFailed,
        onAnimationComplete
    }: ReturnType<typeof streamerHook> = streamerHook()

    const {
        shouldRender: shouldRenderBorder,
        path: borderPath,
        size: borderSize,
        messageRef
    }: ReturnType<typeof streamingBorderHook> = streamingBorderHook({
        isDone: isDone && !isStreamingFailedError
    })

    return isProgressing || isDone || isStreamingFailedError ? (
        <div
            className={classes.container({
                author: EMessageAuthor.AI,
                isNotInteractive: true,
                isLight: false
            })}
        >
            <div className={classes.messageRow({ author: EMessageAuthor.AI })}>
                <div className={classes.messageWrapper()}>
                    <div
                        ref={messageRef}
                        className={classes.message({
                            author: EMessageAuthor.AI,
                            appearance: "progressing"
                        })}
                    >
                        {isStringPlainText(messageText as string) ? (
                            <Typography
                                text={messageText}
                                color={isStreamingFailedError ? ETypographyColor.Blue300 : ETypographyColor.White}
                                isPretty
                                withHyphens
                            />
                        ) : (
                            renderMessage(messageText as string)
                        )}

                        <Retrier handleAction={onStreamingFailed} isVisible={isStreamingFailedError} />
                    </div>

                    <AnimatePresence initial={false}>
                        {shouldRenderBorder && borderPath && (
                            <m.svg
                                key="border-svg"
                                className={classes.svgElem()}
                                viewBox={`0 0 ${borderSize.width} ${borderSize.height}`}
                                preserveAspectRatio="none"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ opacity: { duration: 0.2 } }}
                            >
                                <m.path
                                    strokeWidth={1}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    initial={{ pathLength: 0 }}
                                    d={borderPath}
                                    stroke={THEME.COLORS.blue[200]}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.8, ease: "easeInOut", delay: 0.05 }}
                                    onAnimationStart={(): void => void scrollFeedToBottom()}
                                    onAnimationComplete={(): void => onAnimationComplete()}
                                />
                            </m.svg>
                        )}
                    </AnimatePresence>
                </div>

                <owls.DefaultLogo fill="blue-600" />
            </div>
        </div>
    ) : (
        <TypingIndicator withText={isDelayed} />
    )
}

MessageStreamable.displayName = "CoachingChatMessageStreamable"

export { MessageStreamable as CoachingChatMessageStreamable }
